import {
  IDepartmentData,
  IWorkflowFormSubmissionChecklistData,
  IWorkflowFormSubmissionCommentData,
  IWorkflowFormSubmissionData,
  IWorkflowTransitionLogData,
  IUserData,
  IWorkflowFormSubmissionCommentAttachmentData
} from '@/models';
import {
  IWorkflowFormPaginatedListData,
  IWorkflowFormService,
  IUpdateWorkflowFormSubmissionChecklistAssigneePayload,
  IWorkflowFormSubmissionFetchParams,
  IUpdateWorkflowFormSubmissionStatePayload
} from './workflow-form.types';
import { AxiosInstance } from 'axios';
import { objectToFormData } from '@/utils/objectToFormData';
import {
  WORKFLOW_FORM_SUBMISSIONS_EXPORT_PDF_TIMEOUT,
  WORKFLOW_FORM_SUBMISSIONS_EXPORT_XLSX_TIMEOUT
} from './workflow-form.constants';

export class WorkflowFormService implements IWorkflowFormService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/user-apps/submissions/';
  }

  //submissions
  fetchWorkflowFormSubmissions(
    params: IWorkflowFormSubmissionFetchParams
  ): Promise<IWorkflowFormPaginatedListData> {
    return this.axios
      .get<IWorkflowFormPaginatedListData>(`${this.endpoint}`, {
        params
      })
      .then(({ data }) => data);
  }

  fetchWorkflowFormSubmission(
    id: number
  ): Promise<IWorkflowFormSubmissionData> {
    return this.axios
      .get<IWorkflowFormSubmissionData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  updateWorkflowFormSubmissionState(
    data: IUpdateWorkflowFormSubmissionStatePayload
  ): Promise<IWorkflowFormSubmissionData> {
    return this.axios
      .put<IWorkflowFormSubmissionData>(
        `${this.endpoint}${data.id}/set-state/${data.state}/`,
        { assignee: data.assignee, comment: data.comment }
      )
      .then(({ data }) => data);
  }

  createWorkflowFormSubmission(
    data: IWorkflowFormSubmissionData
  ): Promise<IWorkflowFormSubmissionData> {
    const formData = objectToFormData(data);
    return this.axios
      .post<IWorkflowFormSubmissionData>(`${this.endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  updateWorkflowFormSubmission(
    data: IWorkflowFormSubmissionData
  ): Promise<IWorkflowFormSubmissionData> {
    const formData = objectToFormData(data);
    return this.axios
      .patch<IWorkflowFormSubmissionData>(
        `${this.endpoint}${data.id}/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  deleteWorkflowFormSubmission(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  fetchWorkflowFormSubmissionsAvailableAssignees(id: number, state: number) {
    return this.axios
      .get<IUserData[]>(
        `${this.endpoint}${id}/get-available-assignees/${state}/`
      )
      .then(({ data }) => data);
  }

  fetchWorkflowFormSubmissionValidStateTransitions(id: number) {
    return this.axios
      .get(`${this.endpoint}${id}/get-valid-transitions/`)
      .then(({ data }) => data);
  }

  // activities
  fetchWorkflowFormSubmissionActivities(
    id: number
  ): Promise<IWorkflowTransitionLogData[]> {
    return this.axios
      .get<IWorkflowTransitionLogData[]>(`${this.endpoint}${id}/activities/`)
      .then(({ data }) => data);
  }

  //checklist
  fetchWorkflowFormSubmissionChecklists(
    data: IWorkflowFormSubmissionData
  ): Promise<IWorkflowFormSubmissionChecklistData[]> {
    return this.axios
      .get<IWorkflowFormSubmissionChecklistData[]>(
        `${this.endpoint}${data.id}/checklist/`
      )
      .then(({ data }) => data);
  }

  updateWorkflowFormSubmissionChecklist(
    data: IWorkflowFormSubmissionChecklistData
  ): Promise<IWorkflowFormSubmissionChecklistData> {
    return this.axios
      .patch<IWorkflowFormSubmissionChecklistData>(
        `${this.endpoint}${data.form_submission}/checklist/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  markCompleteWorkflowFormSubmissionChecklist(
    data: IWorkflowFormSubmissionChecklistData
  ) {
    return this.axios
      .post<IWorkflowFormSubmissionChecklistData>(
        `${this.endpoint}${data.form_submission}/checklist/${data.id}/set-state/`,
        data
      )
      .then(({ data }) => data);
  }

  updateWorkflowFormSubmissionChecklistAssignee(
    data: IUpdateWorkflowFormSubmissionChecklistAssigneePayload
  ): Promise<IWorkflowFormSubmissionChecklistData> {
    return this.axios
      .post<IWorkflowFormSubmissionChecklistData>(
        `${this.endpoint}${data.form_submission}/checklist/${data.id}/set-assignee/`,
        data
      )
      .then(({ data }) => data);
  }

  fetchChecklistAssigneeOptions(data: IWorkflowFormSubmissionChecklistData) {
    return this.axios
      .get<IUserData[]>(
        `${this.endpoint}${data.form_submission}/checklist/${data.id}/get-assignee-options/`
      )
      .then(({ data }) => data);
  }

  //department
  fetchWorkflowFormSubmissionDepartments(
    data: IWorkflowFormSubmissionData
  ): Promise<IDepartmentData[]> {
    return this.axios
      .get<IDepartmentData[]>(`${this.endpoint}${data.id}/departments/`)
      .then(({ data }) => data);
  }

  // comments
  fetchWorkflowFormSubmissionComments(
    submissionId: number
  ): Promise<IWorkflowFormSubmissionCommentData[]> {
    return this.axios
      .get<IWorkflowFormSubmissionCommentData[]>(
        `${this.endpoint}${submissionId}/comments/`
      )
      .then(({ data }) => data);
  }

  fetchWorkflowFormSubmissionComment(
    submissionId: number,
    commentId: number
  ): Promise<IWorkflowFormSubmissionCommentData> {
    return this.axios
      .get<IWorkflowFormSubmissionCommentData>(
        `${this.endpoint}${submissionId}/comments/${commentId}/`
      )
      .then(({ data }) => data);
  }

  createWorkflowFormSubmissionComment(
    submissionId: number,
    comment: IWorkflowFormSubmissionCommentData
  ): Promise<IWorkflowFormSubmissionCommentData> {
    return this.axios
      .post<IWorkflowFormSubmissionCommentData>(
        `${this.endpoint}${submissionId}/comments/`,
        comment
      )
      .then(({ data }) => data);
  }

  updateWorkflowFormSubmissionComment(
    submissionId: number,
    comment: IWorkflowFormSubmissionCommentData
  ): Promise<IWorkflowFormSubmissionCommentData> {
    return this.axios
      .patch<IWorkflowFormSubmissionCommentData>(
        `${this.endpoint}${submissionId}/comments/${comment.id}/`,
        comment
      )
      .then(({ data }) => data);
  }

  deleteWorkflowFormSubmissionComment(
    submissionId: number,
    commentId: number
  ): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${submissionId}/comments/${commentId}/`)
      .then(({ data }) => data);
  }

  fetchWorkflowFormSubmissionCommentsMentionableUsers(submissionId: number) {
    return this.axios
      .get(`${this.endpoint}${submissionId}/comments/mentionable-users/`)
      .then(({ data }) => data);
  }

  addWorkflowFormSubmissionCommentAttachments(
    submissionId: number,
    commentId: number,
    attachments: IWorkflowFormSubmissionCommentAttachmentData[]
  ): Promise<IWorkflowFormSubmissionCommentData> {
    const formData = objectToFormData({ attachments });
    return this.axios
      .post<IWorkflowFormSubmissionCommentData>(
        `${this.endpoint}${submissionId}/comments/${commentId}/add-attachments/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  removeWorkflowFormSubmissionCommentAttachment(
    submissionId: number,
    commentId: number,
    attachmentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${submissionId}/comments/${commentId}/remove-attachment/${attachmentId}/`
      )
      .then();
  }

  exportWorkflowFormSubmissionPDF(id: number): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}${id}/export-pdf/`, {
        responseType: 'blob',
        timeout: WORKFLOW_FORM_SUBMISSIONS_EXPORT_PDF_TIMEOUT
      })
      .then(({ data }) => data);
  }

  exportWorkflowFormSubmissionXLSX(
    params: IWorkflowFormSubmissionFetchParams
  ): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}export-xlsx/`, {
        params,
        timeout: WORKFLOW_FORM_SUBMISSIONS_EXPORT_XLSX_TIMEOUT,
        responseType: 'blob'
      })
      .then(({ data }) => data);
  }
}
